import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, FormGroup } from 'react-bootstrap';
import fileTypes from '../../../common/fileTypes';
import { getSupportedExts } from '../../utils/file';

const DEFAULT_VIDEO_ENCODE_TYPE_NAME = 'Make Compatible';

/**
 * @typedef {{input_type: 'audio'|'video', name: string}} EncodeType
 */

export default class UploadPrompt extends Component {
  /**
   * returns the default encode type for the given props, or an empty string if no encode types are available
   * @returns {string} the default encode type for the given props, or an empty string if no encode types are available
   */
  getEncodeType = () => {
    const { encodeTypes } = this.props;

    if (!encodeTypes?.length) {
      return '';
    }

    if (encodeTypes.some(item => item.name === DEFAULT_VIDEO_ENCODE_TYPE_NAME)) {
      return DEFAULT_VIDEO_ENCODE_TYPE_NAME;
    }

    return encodeTypes[0].name;
  };

  getInitialState() {
    return { uploadType: 'file', files: [], encodeType: this.getEncodeType() };
  }

  state = this.getInitialState();

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { show } = this.props;
    const { show: nextShow } = nextProps;
    const { encodeType } = this.state;

    if (!encodeType) {
      this.setState({ encodeType: this.getEncodeType() });
    }

    if (show && !nextShow) {
      this.setState(this.getInitialState());
    }
  }

  onChangeUploadType = event =>
    this.setState({ uploadType: event.target.value, files: [], encodeType: this.getEncodeType() });

  onChangeFiles = event => this.setState({ files: Array.from(event.target.files) });

  onChangeEncodeType = event => this.setState({ encodeType: event.target.value });

  /**
   * currently only supports video encoding types, but could be extended to support audio encoding types in the future
   * @returns {Array<JSX.Element>} array of <option> elements for the encode type select dropdown
   */
  getOptions = () => {
    if (!this.hasVideo()) {
      return [];
    }

    const { encodeTypes } = this.props;
    /** @type {EncodeType[]} types */
    const types = encodeTypes || [];
    const makeKey = index => `${index}-${types[index].name}`;

    return types
      .filter(item => item.input_type === 'video')
      .map((item, index) => <option key={makeKey(index)}>{item.name}</option>);
  };

  /**
   * returns true if the given file is a video file, false otherwise
   * @param {File} item - the file to check
   * @returns {boolean} true if the file is a video file, false otherwise
   */
  isVideo = item => fileTypes.video.some(ext => item.name.toLowerCase().endsWith(ext));

  /**
   * returns true if any of the selected files are video files, false otherwise
   * @returns {boolean} true if any of the selected files are video files, false otherwise
   */
  hasVideo = () => {
    const { files } = this.state;

    return files.some(file => this.isVideo(file));
  };

  render() {
    const {
      show,
      title = 'Upload',
      body,
      accept = getSupportedExts(),
      onHide,
      multiple,
      onSuccess,
    } = this.props;
    const { uploadType, files, encodeType } = this.state;

    return (
      <Modal backdrop="static" show={show} onHide={onHide}>
        <Modal.Header>
          <Modal.Title data-test="modal-upload-title">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {body}
          {multiple && (
            <FormGroup>
              <label className="normal block">
                <input
                  type="radio"
                  name="upload"
                  value="file"
                  checked={uploadType === 'file'}
                  onChange={this.onChangeUploadType}
                  className="accent-sunrise"
                />
                <span className="margin">Upload Files</span>
              </label>
              <label className="normal block">
                <input
                  type="radio"
                  name="upload"
                  value="folder"
                  checked={uploadType === 'folder'}
                  onChange={this.onChangeUploadType}
                  className="accent-sunrise"
                />
                <span className="margin">Upload Folder</span>
              </label>
            </FormGroup>
          )}
          {uploadType === 'file' && (
            <FormGroup>
              <input
                id="fileUpload"
                type="file"
                accept={accept}
                onChange={this.onChangeFiles}
                multiple={multiple}
                className="hidden"
              />
              <div className="space-x-2 flex items-center">
                {' '}
                <label
                  htmlFor="fileUpload"
                  className="bg-dark-night text-white cursor-pointer inline-block px-4 py-2 rounded-[15px]"
                >
                  Choose File{multiple ? 's' : ''}
                </label>
                {files.length === 0 && <span id="fileName">No files selected</span>}
                {files.length === 1 && <span id="fileName">{files[0].name}</span>}
                {files.length > 1 && <span id="fileName">{files.length} files selected</span>}
              </div>
            </FormGroup>
          )}
          {multiple && uploadType === 'folder' && (
            <FormGroup>
              <input
                id="folderUpload"
                type="file"
                className="hidden"
                accept={accept}
                onChange={this.onChangeFiles}
                multiple
                ref={node => {
                  if (!node) {
                    return;
                  }

                  node.webkitdirectory = true;
                  node.directory = true;
                }}
              />
              <div className="space-x-2 flex items-center">
                {' '}
                <label
                  htmlFor="folderUpload"
                  className="bg-dark-night text-white cursor-pointer inline-block px-4 py-2 rounded-[15px]"
                >
                  Choose Folder
                </label>
                {files.length === 0 && <span id="fileName">No files selected</span>}
                {files.length === 1 && <span id="fileName">{files[0].name}</span>}
                {files.length > 1 && <span id="fileName">{files.length} files selected</span>}
              </div>
            </FormGroup>
          )}
          {this.hasVideo() && (
            <FormGroup>
              <label>Video Encoding type</label>
              <select
                value={encodeType}
                className="form-control"
                onChange={this.onChangeEncodeType}
              >
                {this.getOptions()}
              </select>
            </FormGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button data-test="modal-upload-button-cancel" className="btn-cancel" onClick={onHide}>
            Cancel
          </Button>
          <Button
            data-test="modal-upload-button-upload"
            className="btn-upload"
            bsStyle="primary"
            onClick={() => onSuccess(multiple ? files : files[0], encodeType)}
            disabled={!files.length}
          >
            Upload
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

UploadPrompt.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string,
  body: PropTypes.string,
  accept: PropTypes.string,
  encodeTypes: PropTypes.arrayOf(
    PropTypes.shape({
      input_type: PropTypes.oneOf(['audio', 'video']).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  onSuccess: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
};
